import { CaretDownOutlined, CaretUpOutlined, MonitorOutlined } from "@ant-design/icons";
import { IDashboardOutputConfig } from "@kuika/kuika-cl-model";
import { memo, useState } from "react";
import { Handle, Position, useReactFlow } from "reactflow";
import KuikaSvg from "../../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { NODE_TOOLTIPS } from "./nodesInfoContent";
import baseStyles from "./BaseNode.module.css";

interface DashboardOutputNodeData {
  type: "DashboardOutput";
  title: string;
  config?: IDashboardOutputConfig;
  status?: "idle" | "streaming" | "active";
}

interface DashboardOutputNodeProps {
  data: DashboardOutputNodeData;
  id: string;
  selected?: boolean;
}

const NODE_CATEGORY_STYLES = {
  output: {
    primaryColor: "#cf3112",
    headerBackground: "rgba(207, 49, 18, 0.1)",
    iconColor: "#cf3112",
    handleColor: "#cf3112"
  }
};

const DashboardOutputNode = ({ data, id, selected }: DashboardOutputNodeProps) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(data.status || "idle");
  const [config, setConfig] = useState<IDashboardOutputConfig>(() => {
    // Default config values matching backend VisionDataConfigModel
    const defaults: IDashboardOutputConfig = {
      widgets: [],
      enableThumbnails: true,
      websocketUrl: ""
    };
    // Merge with provided config from template
    if (data.config) {
      return { ...defaults, ...data.config };
    }
    return defaults;
  });
  const { setNodes } = useReactFlow();

  const categoryStyles = NODE_CATEGORY_STYLES.output;

  const updateConfig = (updates: Partial<IDashboardOutputConfig>) => {
    const newConfig = { ...config, ...updates };
    setConfig(newConfig);

    setNodes((nodes) =>
      nodes.map((node) => (node.id === id ? { ...node, data: { ...node.data, config: newConfig } } : node))
    );
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNodes((nodes) => nodes.filter((node) => node.id !== id));
  };

  const renderInfoIcon = () => (
    <div className={baseStyles.nodeInfoTopRight}>
      <KuikaTooltip
        position="top"
        content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.dashboardOutput.text}</div>}
      >
        <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
          <KuikaSvg name="info-circle" width={16} height={16} />
        </span>
      </KuikaTooltip>
    </div>
  );

  const renderSummary = () => {
    return (
      <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
        {renderInfoIcon()}
        <span className={baseStyles.collapsedSummary}>
          {config.widgets?.length || 0} widgets • {status}
        </span>
      </div>
    );
  };

  return (
    <div className={`${baseStyles.nodeContainer} ${selected ? "selected" : ""}`}>
      {selected && (
        <button
          className={`${baseStyles.deleteButton} ${baseStyles.nodrag}`}
          onClick={handleDelete}
          onMouseDown={(e) => e.stopPropagation()}
          title="Delete node"
        >
          <KuikaSvg type="common" name="x" width={14} height={14} color="var(--red-5)" />
        </button>
      )}
      <div
        className={`${baseStyles.nodeHeader} ${expanded ? baseStyles.nodeHeaderExpanded : ""}`}
        style={{ background: categoryStyles.headerBackground }}
      >
        <span className={baseStyles.nodeIcon} style={{ color: categoryStyles.iconColor }}>
          <MonitorOutlined />
        </span>
        <span className={baseStyles.nodeTitle}>
          {data.title}
          <KuikaTooltip
            position="top"
            content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.dashboardOutput.text}</div>}
          >
            <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
              <KuikaSvg name="info-circle" width={16} height={16} />
            </span>
          </KuikaTooltip>
        </span>
        <button
          onClick={() => setExpanded(!expanded)}
          onMouseDown={(e) => e.stopPropagation()}
          className={`${baseStyles.expandButton} ${baseStyles.nodrag}`}
        >
          {expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </button>
      </div>

      {!expanded && renderSummary()}

      {expanded && (
        <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
          {renderInfoIcon()}
          <div className={baseStyles.nodeForm}>
            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>WebSocket URL</label>
              <input
                type="text"
                className={`${baseStyles.formInput} ${baseStyles.nodrag}`}
                value={config.websocketUrl || ""}
                onChange={(e) => updateConfig({ websocketUrl: e.target.value })}
                placeholder="wss://your-server.com/dashboard"
              />
            </div>

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={config.enableThumbnails || false}
                  onChange={(e) => updateConfig({ enableThumbnails: e.target.checked })}
                  className={baseStyles.nodrag}
                />
                Enable Thumbnails
              </label>
            </div>

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Widgets ({config.widgets?.length || 0})</label>
              {config.widgets && config.widgets.length > 0 ? (
                <div className={baseStyles.itemList}>
                  {config.widgets.map((widget, index) => (
                    <div key={widget.id || index} className={baseStyles.itemCard}>
                      <div className={baseStyles.widgetTitle}>{widget.title || "Untitled Widget"}</div>
                      <div className={baseStyles.widgetDetails}>
                        <span>
                          Type: <strong>{widget.type}</strong>
                        </span>
                        <span>
                          Metric: <strong>{widget.metric || "N/A"}</strong>
                        </span>
                        {widget.refreshInterval && (
                          <span>
                            Refresh: <strong>{widget.refreshInterval}s</strong>
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <span className={`${baseStyles.collapsedSummary} ${baseStyles.emptyState}`}>No widgets configured</span>
              )}
            </div>

            <button
              className={`${baseStyles.formButton} ${baseStyles.nodrag}`}
              onClick={() => alert("Widget configuration would open in a modal")}
            >
              Configure Widgets
            </button>

            <button
              className={`${baseStyles.formButton} ${baseStyles.nodrag}`}
              onClick={() => setStatus(status === "active" ? "idle" : "active")}
            >
              {status === "active" ? "Stop" : "Start"} Streaming
            </button>
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className={baseStyles.targetHandle}
        style={{ background: categoryStyles.handleColor }}
      />
      <Handle
        type="source"
        position={Position.Right}
        className={baseStyles.sourceHandle}
        style={{ background: categoryStyles.handleColor }}
      />
    </div>
  );
};

export default memo(DashboardOutputNode);
